import HttpClient from "../../../../core/HttpClient";
import { Authentication } from "@leedpulse-monorepo/classes";
import { VoipCallRow } from "./listVoipCalls";

/** `fact` — it happened. `deadline` — a timer was armed for this instant and
 *  may never have fired. */
export type VoipEventKind = "fact" | "deadline";

export interface VoipTimelineEvent {
    ts: number;
    label: string;
    extra?: string;
    kind: VoipEventKind;
    /** Shipped by the backend with every event. The page used to keep its own
     *  copy of these explanations, which drifted from the builder. */
    explanation: string;
}

export interface VoipAnomaly {
    code: string;
    severity: "error" | "warn";
    detail: string;
}

export interface VoipCallDetail extends VoipCallRow {
    roomSid: string | null;
    sipCallId: string | null;
    sipCallIdFull: string | null;
    ownerUid: string | null;
    contactId: string | null;
    recordingPhase: string | null;
    resolutionStatus: string | null;
    lastLeftKind: string | null;
    urlRecording: string | null;
    /** Root `recorded` — `false` after a short-call triage, which then masks
     *  `url_recording*` behind sentinels while the nested storage path
     *  (`recordingStoragePath`) stays playable (LEED-1568). Null when the doc
     *  predates the field. */
    recorded: boolean | null;
    /** Nested storage path the backend can still stream even when `recorded
     *  === false` hid it behind the root sentinels. */
    recordingStoragePath: string | null;
    /** Triage summary explaining why `recorded === false`. Null unless
     *  `recorded === false`. */
    recordingHiddenReason: string | null;
    hasTranscription: boolean;
    timeline: VoipTimelineEvent[];
    failures: string[];
    /** Reference sequence the call was graded against. */
    scenario: string;
    scenarioLabel: string;
    /** Departures from that sequence. Distinct from `failures`, which only
     *  lists errors the backend explicitly stamped. */
    anomalies: VoipAnomaly[];
}

/** Fetches the full timeline + failure surface for a single LiveKit call. */
async function getVoipCall(
    auth: Authentication | null,
    roomName: string,
): Promise<VoipCallDetail | null> {
    if (!auth || !roomName) return null;

    try {
        const response = await HttpClient.get(`/leedflow/voip-calls/${encodeURIComponent(roomName)}`, auth.token);
        return response as VoipCallDetail;
    } catch (error) {
        console.error(error);
        return null;
    }
}

export default getVoipCall;
